import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Divider,
  Button,
  MenuItem,
  Menu,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { useGetApi } from "../hooks/useGetApi";
import { getSupplierApi } from "../services/supplier.service";
import { AddCircleOutlineOutlined, MoreVert } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { showDeleteButton, showCreateButton, showUpdateButton } from "../utils/permissionHelper";

const headLabel = [
  {
    id: 1,
    label: "Supplier Name",
    value: "supplierName",
  },
  {
    id: 2,
    label: "Contact Person",
    value: "contactPerson",
  },
  {
    id: 3,
    label: "Contact No",
    value: "contactNo",
  },
  {
    id: 4,
    label: "Email",
    value: "email",
  },
  {
    id: 5,
    label: "City",
    value: "city",
  },
  {
    id: 6,
    label: "GST No",
    value: "gstNo",
  },
  {
    id: 7,
    label: "Status",
    value: "active",
  },
  {
    id: 8,
    label: "",
    value: "",
    align: "right",
  },
];

// Dynamic popover options based on permissions will be calculated in component

const MasterSupplier = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  // Dynamic popover options based on permissions
  const getPopoverOptions = () => {
    const options = [];
    if (showUpdateButton(user, "Suppliers")) {
      options.push("Edit Supplier");
    }
    if (showDeleteButton(user, "Suppliers")) {
      options.push("Delete Supplier");
    }
    return options;
  };

  const popoverOptions = getPopoverOptions();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("supplierName");
  const [selectedRow, setSelectedRow] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_LIMIT });
  const [sortModel, setSortModel] = useState([{ field: "supplierName", sort: "asc" }]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);

  const searchByOptions = [
    { value: "supplierName", label: "Supplier Name" },
    { value: "contactPerson", label: "Contact Person" },
    { value: "contactNo", label: "Contact No" },
    { value: "email", label: "Email" },
    { value: "city", label: "City" },
  ];
  const searchByLabel = searchByOptions.find((o) => o.value === searchBy)?.label || "Supplier Name";

  const handleNewSupplier = () => {
    if (showCreateButton(user, "Suppliers")) {
      navigate("add");
    }
  };

  const handlePopoverClick = async (option) => {
    handleClose();
    if (option === popoverOptions[0]) {
      // Edit supplier - ensure we pass the ID as string
      if (selectedRow?._id && showUpdateButton(user, "Suppliers")) {
        navigate(selectedRow._id);
      }
    } else if (option === popoverOptions[1]) {
      // Delete supplier - Only for Super Admin or users with Suppliers.Delete permission
      if (selectedRow?._id && showDeleteButton(user, "Suppliers") && 
          window.confirm("Are you sure you want to delete this supplier?")) {
        try {
          const { deleteSupplierApi } = await import("../services/supplier.service");
          const response = await deleteSupplierApi(selectedRow._id);
          
          if (response?.success || response?.isSuccess) {
            toast.success("Supplier deleted successfully");
            refetch(); // Refresh the list
          } else {
            toast.error(response?.message || "Failed to delete supplier");
          }
        } catch (error) {
          console.error("Delete supplier error:", error);
          toast.error("An error occurred while deleting supplier");
        }
      }
    }
  };

  const handleRightClick = (event, row) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row);
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleCloseModal = () => {
    navigate(-1); // Navigate back to previous page
  };

  const { dataList: supplierList, dataCount: supplierCount, isLoading, isError, refetch } = useGetApi({
    apiFunction: getSupplierApi,
    params: { limit: paginationModel.pageSize, page: paginationModel.page + 1, search, searchBy, active: true, sort: sortModel[0]?.field || "supplierName", order: sortModel[0]?.sort || "asc" },
    dependencies: [search, searchBy, paginationModel.pageSize, paginationModel.page, sortModel],
    debounceDelay: 500,
    dataKey: "suppliers",
    countKey: "total",
  });

  const rawRows = (supplierList || []).map((row, index) => ({
    id: row._id || index,
    _id: row._id,
    supplierName: row?.supplierName || "-",
    contactPerson: row?.contactPerson || "-",
    contactNo: row?.contactNo || "-",
    email: row?.email || "-",
    city: row?.city || "-",
    gstNo: row?.gstNo || "-",
    status: row?.active ? "Active" : "Inactive",
    _original: row,
  }));

  const sortField = sortModel[0]?.field || "supplierName";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = [...rawRows].sort((a, b) => { const cmp = String(a[sortField] ?? "").localeCompare(String(b[sortField] ?? ""), undefined, { numeric: true }); return sortOrder === "asc" ? cmp : -cmp; });
  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;
  const rows = sortedRows.length > pageSize ? sortedRows.slice(page * pageSize, page * pageSize + pageSize) : sortedRows;

  const getColumns = useCallback(() => [
    { field: "supplierName", headerName: "Supplier Name", flex: 1, minWidth: 120, headerAlign: "left", align: "left", sortable: true },
    { field: "contactPerson", headerName: "Contact Person", flex: 1, minWidth: 120, headerAlign: "left", align: "left", sortable: true },
    { field: "contactNo", headerName: "Contact No", flex: 1, minWidth: 110, headerAlign: "left", align: "left", sortable: true },
    { field: "email", headerName: "Email", flex: 1, minWidth: 140, headerAlign: "left", align: "left", sortable: true },
    { field: "city", headerName: "City", flex: 0.8, minWidth: 90, headerAlign: "left", align: "left", sortable: true },
    { field: "gstNo", headerName: "GST No", flex: 0.8, minWidth: 100, headerAlign: "left", align: "left", sortable: true },
    { field: "status", headerName: "Status", flex: 0.6, minWidth: 90, headerAlign: "center", align: "center", sortable: true, renderCell: (params) => <Chip label={params.value || "Active"} color={params.value === "Inactive" ? "error" : "success"} size="small" variant="outlined" /> },
    { field: "actions", headerName: "Actions", flex: 0, width: 70, minWidth: 60, headerAlign: "center", align: "center", sortable: false, renderCell: (params) => popoverOptions.length > 0 ? <IconButton size="small" onClick={(e) => handleClick(e, params.row._original || params.row)}><MoreVert /></IconButton> : null },
  ], [popoverOptions.length]);

  const dataGridSx = {
    border: "none", "& .MuiDataGrid-root": { border: "none", width: "100%" }, "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0", padding: isMobile ? "4px" : "8px", fontSize: isMobile ? "12px" : "14px" },
    "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }, "& .MuiDataGrid-columnHeader": { backgroundColor: "#f5f5f5", fontWeight: "bold" },
    "& .MuiDataGrid-row": { "&:hover": { backgroundColor: "#f5f5f5" }, "&:nth-of-type(odd)": { backgroundColor: "#fafafa" }, "&:nth-of-type(even)": { backgroundColor: "white" } },
    "& .MuiDataGrid-footerContainer": { borderTop: "2px solid #e0e0e0", backgroundColor: "#f9f9f9" },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", paddingBottom: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ color: "#5D6A82", textTransform: "none", fontSize: "16px", fontWeight: "bold", padding: 0, transition: "none !important", mt: 2 }}>Back</Button>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 2 }}>
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>Supplier Master</Typography>
          </Box>
          <Box sx={{ my: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 2 }}>
            <TextField placeholder={`Search by ${searchByLabel}`} size="small" sx={{ width: "300px" }} slotProps={{ input: { startAdornment: (
              <InputAdornment position="start">
                <select className="uid-select" value={searchBy} onChange={(e) => setSearchBy(e.target.value)} style={{ border: "none", outline: "none", background: "transparent", cursor: "pointer", color: "#333", fontWeight: "500" }}>
                  {searchByOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Search />
              </InputAdornment>
            ) } }} value={search || ""} onChange={(e) => setSearch(e.target.value || "")} />
            {showCreateButton(user, "Suppliers") && (
              <Button variant="contained" sx={{ textTransform: "none", display: "flex", alignItems: "center", gap: 0.5 }} startIcon={<AddCircleOutlineOutlined />} onClick={handleNewSupplier}>Add Supplier</Button>
            )}
          </Box>
          {isLoading ? <Loader /> : isError ? <ErrorMessage /> : (
            <>
              <Box sx={{ height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" }, width: "100%", minHeight: "300px", overflow: "hidden" }}>
                <DataGrid rows={rows} columns={getColumns()} paginationModel={paginationModel} onPaginationModelChange={(m) => setPaginationModel(m)} pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS} paginationMode="server" rowCount={supplierCount} sortingMode="server" sortModel={sortModel} onSortModelChange={(m) => setSortModel(m)} disableRowSelectionOnClick loading={isLoading} sx={dataGridSx} components={{ NoRowsOverlay: () => <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><Typography variant="body2" color="text.secondary">No suppliers found</Typography></Box> }} />
              </Box>
              <Menu
                id={Boolean(anchorEl) ? "row-popover" : undefined}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
                anchorPosition={
                  anchorPosition
                    ? { top: anchorPosition.top, left: anchorPosition.left }
                    : undefined
                }
                anchorOrigin={{
                  vertical: "center",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                {popoverOptions?.map((option, index) => (
                  <MenuItem 
                    key={index} 
                    onClick={() => handlePopoverClick(option)}
                  >
                    {option || ""}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default MasterSupplier; 